import { ItemType, ItemLocation } from '../schema';
import { availabilityStatus as availabilityStatusFromLogic } from './inventoryCheckLogic';

export interface LocationData {
    storageLocations: { id: string; name: string; quantity: number }[];
    events: { eventId: string; eventName: string; quantity: number }[];
    total: number;
}

type ItemLocationFromItemType = {
    id: string;
    quantity: number;
    event: { id: string; name?: string } | null | undefined;
    storageLocation?: { id: string; name?: string } | null;
};

/**
 * Calculate item locations breakdown (by storage location and by event)
 */
export function calculateItemLocations(itemType: ItemType, locations: (ItemLocation | ItemLocationFromItemType)[]): LocationData {
    const storageLocations: { id: string; name: string; quantity: number }[] = [];
    const events: { eventId: string; eventName: string; quantity: number }[] = [];

    for (const location of locations) {
        const qty = location.quantity || 0;
        if (location.storageLocation) {
            const storage = location.storageLocation;
            const name = storage.name || 'Storage';
            const existing = storageLocations.find(s => s.id === storage.id);
            if (existing) {
                existing.quantity += qty;
            } else {
                storageLocations.push({
                    id: storage.id,
                    name,
                    quantity: qty,
                });
            }
        } else if (location.event) {
            const eventName = location.event.name || 'Unknown Event';
            events.push({
                eventId: location.event.id,
                eventName,
                quantity: qty,
            });
        }
        // legacy: no event and no storageLocation treated as "HQ" - sum into first storage or skip (caller should have storage locations)
    }

    const total = storageLocations.reduce((s, x) => s + x.quantity, 0) + events.reduce((s, e) => s + e.quantity, 0);
    return { storageLocations, events, total };
}

/**
 * Get location name (HQ or event name)
 */
export function getLocationName(eventId: string | null | undefined, eventName?: string): string {
    if (!eventId) {
        return 'HQ';
    }
    return eventName || 'Unknown Event';
}

/**
 * Calculate available quantity for an itemType, excluding items at a specific event or storage location
 */
export function calculateAvailableQuantity(
    itemTypeId: string,
    itemLocations: Array<{
        id: string;
        quantity: number;
        itemType?: { id: string } | null;
        event?: { id: string } | null;
        storageLocation?: { id: string } | null;
    }>,
    excludeEventId?: string | null,
    excludeStorageLocationId?: string | null
): number {
    let total = 0;
    for (const location of itemLocations) {
        if (location.itemType?.id !== itemTypeId) continue;
        if (excludeEventId && location.event?.id === excludeEventId) continue;
        if (excludeStorageLocationId && location.storageLocation?.id === excludeStorageLocationId) continue;
        total += location.quantity || 0;
    }
    return total;
}

export interface AvailabilityResult {
    available: number;
    required: number;
    shortage: number;
    status: 'sufficient' | 'low' | 'insufficient';
}

/**
 * Calculate availability status for an itemType
 * Returns availability information including status (sufficient/low/insufficient)
 */
export function calculateAvailability(
    itemTypeId: string,
    requiredQty: number,
    itemLocations: Array<{
        id: string;
        quantity: number;
        itemType?: { id: string } | null;
        event?: { id: string } | null;
        storageLocation?: { id: string } | null;
    }>,
    excludeEventId?: string | null,
    excludeStorageLocationId?: string | null
): AvailabilityResult {
    const available = calculateAvailableQuantity(itemTypeId, itemLocations, excludeEventId, excludeStorageLocationId);
    const shortage = Math.max(0, requiredQty - available);
    const status = availabilityStatusFromLogic(requiredQty, available, 0.8);

    return {
        available,
        required: requiredQty,
        shortage,
        status,
    };
}
